import { Alert } from '@mui/material';
import { useKeycloak } from '@react-keycloak/web';
import { Fragment, useCallback, useEffect, useState } from 'react';
import ReactPaginate from 'react-paginate';
import { translate } from 'react-switch-lang';
import { Card, CardBody, CardHeader, Col, Container, Row } from 'reactstrap';
import { DeleteButtonIcon } from 'src/component/Buttons/DeleteButtonIcon';
import SweetAlert from 'sweetalert2';
import { useHttpClient } from '../../../../../../security/hooks/axiosProvider';
import Breadcrumb from '../../../../breadcrumb';
import styles from '../../../../styles/style-setting.module.scss';
import CreateSousActivity from '../create-sous-activity/create-sous-activity';
import EditSousActivity from '../edit-sous-activity/edit-sous-activity';
import SousActivityColumns from './table-header/sous-activity-columns';

// number of subactivities per page
const NUMBER_OF_SUBACTIVITIES_PER_PAGE = 15;

export const ListSousActivityPage = (props) => {
  const [subActivities, setSubActivities] = useState([]);
  const httpClient = useHttpClient(true);
  const [pageNumber, setPageNumber] = useState(0);
  const activityPerPage = NUMBER_OF_SUBACTIVITIES_PER_PAGE;
  const pagesVisited = pageNumber * activityPerPage;
  const keycloak = useKeycloak().keycloak;
  const [role, setRole] = useState('');
  const [search, setSearch] = useState('');
  const [filterByActivity, setFilterByActivity] = useState('');
  const [activities, setActivities] = useState([]);
  let data = [];
  const changePage = ({ selected }) => {
    setPageNumber(selected);
  };
  const [spinner, setSpinner] = useState({ state: false, id: '' });
  const pageCount = Math.ceil(
    (search !== ''
      ? subActivities.filter((subActivity) => subActivity.name.includes(search))
      : filterByActivity !== ''
      ? subActivities.filter((subActivity) => subActivity.activity.name.includes(filterByActivity))
      : subActivities
    ).length / activityPerPage
  );

  const getSubActivities = useCallback(async () => {
    httpClient.current?.get(`/api/subactivity/all`).then((response) => {
      setSubActivities(response.data);
    });
  }, [httpClient]);

  const deleteSubActivities = async (subActivityId) => {
    await httpClient.current?.delete(`/api/subactivity/${subActivityId}`);
    getSubActivities();
  };

  const handleCheckBox = async (event, subActivity) => {
    setSpinner({ state: true, id: subActivity.id });
    const activityValidation = {
      id: subActivity.id,
      name: subActivity.name,
      activity: subActivity.activity,
      valid: event.target.checked,
    };
    const response = httpClient.current?.put(`/api/subactivity/update`, activityValidation);
    if ((await response).status === 200) {
      getSubActivities();
      setSpinner({ state: false, id: subActivity.id });
    }
  };

  useEffect(() => {
    const getActivities = () => {
      httpClient.current?.get(`/api/activity/all`).then((response) => {
        setActivities(response.data);
      });
    };
    getActivities();
    getSubActivities();
    if (keycloak.authenticated) {
      keycloak.hasResourceRole('ROLE_AGRISED_ADMIN_SUPER')
        ? setRole('super admin')
        : keycloak.hasResourceRole('ROLE_AGRISED_ADMIN')
        ? setRole('admin')
        : setRole('user');
    }
  }, [getSubActivities, httpClient, keycloak]);

  let restPagination = subActivities
    .filter((subActivity) => subActivity.name.toUpperCase().includes(search.toUpperCase()))
    .filter((subActivity) => subActivity.activity.name.toUpperCase().includes(filterByActivity.toUpperCase()))
    .slice(pagesVisited, pagesVisited + activityPerPage);

  restPagination.map((subActivity) => {
    let action = (
      <div>
        <span>
          <EditSousActivity subActivity={subActivity} subActivities={getSubActivities} />
        </span>
        <span>
          <DeleteButtonIcon
            title={props.t('delete_sub_activity')}
            onClick={() =>
              SweetAlert.fire({
                title: props.t('you_delete'),
                text: props.t('not_recover'),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Ok',
                cancelButtonText: props.t('cancel'),
                reverseButtons: true,
              }).then((result) => {
                if (result.value) {
                  deleteSubActivities(subActivity.id);
                  SweetAlert.fire(props.t('Deleted!'), props.t('isDeleted'), 'success');
                } else {
                  SweetAlert.fire(props.t('isNotDeleted'));
                }
              })
            }
            className={`fa fa-trash ${styles['trash']}`}
          />
        </span>
        <div></div>
      </div>
    );
    let status =
      spinner.state === true && spinner.id === subActivity.id ? (
        <div className={`loader-box  ${styles['spinner']}`}>
          <div className="loader-2"></div>
        </div>
      ) : (
        <input
          type="checkbox"
          disabled={role === 'admin'}
          name="status"
          checked={subActivity?.valid}
          onChange={(e) => handleCheckBox(e, subActivity)}
        />
      );

    return data.push({
      id: subActivity.id,
      name: subActivity.name,
      status: status,
      action: action,
    });
  });

  return (
    <div>
      <Fragment>
        <Breadcrumb parent={props.t('sub_activity_url')} url={'settings/sub-activity'} />
        <Container fluid={true}>
          <Row>
            <Col sm="12">
              <Card>
                <CardHeader>
                  <div className="float-right">
                    <CreateSousActivity subActivities={getSubActivities} />
                  </div>
                </CardHeader>
                <CardBody>
                  <div className="row">
                    <div className="col">
                      <input
                        className="form-control mb-2"
                        onChange={(event) => {
                          setSearch(event.target.value);
                          changePage({ selected: 0 });
                        }}
                        value={search}
                        placeholder={props.t('search_unit')}
                      />
                    </div>
                    <div className="col">
                      <select
                        name="activity"
                        id="subActivity_activity"
                        value={filterByActivity}
                        className="form-control mb-2"
                        onChange={(event) => {
                          setFilterByActivity(
                            event.target.value === 'Choose an activity'
                              ? ''
                              : event.target.value === 'Choisissez une activité'
                              ? ''
                              : event.target.value
                          );
                          changePage({ selected: 0 });
                        }}
                      >
                        <option>{props.t('select_activity')}</option>
                        {activities.map((activity) => (
                          <option key={activity.id} value={activity.name}>
                            {activity.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  {data.length === 0 ? (
                    <Alert variant="outlined" severity="info">
                      {props.t('no_sub_activity')}
                    </Alert>
                  ) : (
                    <SousActivityColumns data={data} />
                  )}
                  <br />
                  <div className="float-right">
                    {subActivities.length > NUMBER_OF_SUBACTIVITIES_PER_PAGE && (
                      <ReactPaginate
                        previousLabel="<"
                        nextLabel=">"
                        pageCount={pageCount}
                        onPageChange={changePage}
                        containerClassName={styles['paginationButton']}
                        previousLinkClassName={styles['previousBtn']}
                        nextLinkClassName={styles['nextBtn']}
                        disabledClassName={styles['paginationDisabled']}
                        activeClassName={styles['paginationActive']}
                      />
                    )}
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </Fragment>
    </div>
  );
};
export default translate(ListSousActivityPage);
